// Run status and result (requirements 5.4-5.9, 6.3, 6.4). Set and set-preview runs show the set result view, the
// set name, and its version or "Draft set" (blueprint-sets Req 3.7, 4.10, 11.1).
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator, { type StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import {
  allowedValuesByPath,
  PREVIEW_LABEL,
  previewView,
  type Run,
  type RunStatus,
  type VersionRef,
} from '@bda/shared';
import { api } from '../../api/client';
import { useBlueprint, useRun, useRunResult, useVersion } from '../../api/hooks';
import { DocumentViewer } from '../../components/DocumentViewer';
import { ResultView } from './ResultView';
import { DRAFT_SET_LABEL, PREVIEW_SET_LABEL, SetResultView } from '../sets/SetResultView';
import { setsEnabled } from '../../config';
import { safeName } from '../../lib/download';
import { formatDate, formatDuration, versionLabel } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';

/** A Set_Version as the run pages show it: "Version N", "Draft set", or '-' (a set preview has none). */
export const setVersionLabel = (v: VersionRef | null | undefined): string =>
  v === 'DRAFT' ? DRAFT_SET_LABEL : v === null || v === undefined ? '-' : `Version ${v}`;

/** Runs whose result is a packet split across a set's Members (blueprint-sets). */
export const isSetResultMode = (mode: Run['mode']): boolean => mode === 'set' || mode === 'setPreview';

const STATUS: Record<RunStatus, { type: StatusIndicatorProps.Type; text: string }> = {
  UPLOADING: { type: 'in-progress', text: 'Uploading' },
  QUEUED: { type: 'pending', text: 'Queued' },
  PROCESSING: { type: 'loading', text: 'Processing' },
  SUCCEEDED: { type: 'success', text: 'Succeeded' },
  FAILED: { type: 'error', text: 'Failed' },
};

export const RunStatusIndicator = ({ status }: { status: RunStatus }) => (
  <StatusIndicator type={STATUS[status].type}>{STATUS[status].text}</StatusIndicator>
);

export function RunResultPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const run = useRun(id);
  const result = useRunResult(id, run.data?.status === 'SUCCEEDED');
  // The schema the run used, for choice fields' allowed values: the version's, or the draft's working schema.
  const bpId = run.data?.blueprintId ?? undefined;
  const runVersion = run.data?.version;
  const blueprint = useBlueprint(runVersion === 'DRAFT' ? bpId : undefined);
  const version = useVersion(bpId ?? '', typeof runVersion === 'number' && bpId ? runVersion : null);
  const isPreview = run.data?.mode === 'preview';
  const isSetRun = !!run.data && isSetResultMode(run.data.mode);
  const isSetPreview = run.data?.mode === 'setPreview';
  // A preview run carries its stored Preview_Schema in the result (Req 6.4); it may be gone, in which case the
  // plain result view is shown (still labeled as a preview).
  const previewSchema = isPreview && result.data && !result.data.expired ? result.data.schema : undefined;
  const runSchema = isPreview
    ? previewSchema
    : runVersion === 'DRAFT'
      ? blueprint.data?.blueprint.workingSchema
      : version.data?.schema;
  const allowed = useMemo(() => (runSchema ? allowedValuesByPath(runSchema) : undefined), [runSchema]);
  const preview = useMemo(
    () =>
      previewSchema && result.data && !result.data.expired ? previewView(previewSchema, result.data.result) : undefined,
    [previewSchema, result.data],
  );
  const [goTo, setGoTo] = useState<{ page: number; seq: number } | undefined>();
  if (run.error) return <Alert type="error">{errorMessage(run.error)}</Alert>;
  if (!run.data) return <Spinner size="large" />;
  const r: Run = run.data;

  const showPage =
    r.contentType === 'application/pdf'
      ? (page: number) => setGoTo((g) => ({ page, seq: (g?.seq ?? 0) + 1 }))
      : undefined;

  const retry = async () => {
    try {
      const next = await api.retryRun(r.id);
      navigate(`/runs/${next.id}`);
    } catch (e) {
      notify.error(errorMessage(e));
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={r.fileName}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {r.mode === 'set' && r.setId && setsEnabled() && (
                <Button onClick={() => navigate(`/sets/${r.setId}`)}>Open set</Button>
              )}
              {!isPreview && r.blueprintId && (
                <Button onClick={() => navigate(`/blueprints/${r.blueprintId}`)}>Open blueprint</Button>
              )}
              {!isPreview && r.blueprintId && r.status === 'SUCCEEDED' && typeof r.version === 'number' && (
                <Button
                  onClick={() =>
                    navigate(`/packages/new?blueprintId=${r.blueprintId}&version=${r.version}&sample=${r.id}`)
                  }
                >
                  Create handoff package
                </Button>
              )}
              {!isPreview && r.blueprintId && !r.expired && r.mode === 'custom' && (
                <Button onClick={() => navigate(`/blueprints/${r.blueprintId}/edit?testRun=${r.id}`)}>
                  Edit blueprint with this document
                </Button>
              )}
              {(r.status === 'FAILED' || r.status === 'SUCCEEDED') && !r.expired && (
                <Button onClick={() => void retry()}>{r.status === 'FAILED' ? 'Retry' : 'Re-run'}</Button>
              )}
            </SpaceBetween>
          }
        >
          {isSetRun ? (
            <>
              {r.setName ?? r.blueprintName}{' '}
              {r.mode === 'set' && r.setVersion === 'DRAFT' && <Badge color="grey">{DRAFT_SET_LABEL}</Badge>}
              {isSetPreview && <Badge color="blue">{PREVIEW_SET_LABEL}</Badge>}
            </>
          ) : (
            <>
              {r.blueprintName} {r.version === 'DRAFT' && <Badge color="blue">Draft blueprint</Badge>}
              {isPreview && <Badge color="blue">{PREVIEW_LABEL}</Badge>}
            </>
          )}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container>
          <KeyValuePairs
            columns={4}
            items={[
              { label: 'Status', value: <RunStatusIndicator status={r.status} /> },
              { label: 'Version', value: isSetRun ? setVersionLabel(r.setVersion) : versionLabel(r.version) },
              { label: 'Started', value: formatDate(r.startedAt) },
              { label: 'Duration', value: formatDuration(r.durationMs) },
              { label: 'Pages', value: r.pages ?? '-' },
            ]}
          />
        </Container>
        {r.status === 'FAILED' && (
          <Alert type="error" header="The run failed">
            {r.failureReason ?? 'BDA reported a failure.'}
          </Alert>
        )}
        {r.expired && <Alert type="info">Document and result expired.</Alert>}
        {r.version === 'DRAFT' && r.status === 'SUCCEEDED' && (
          <Alert type="info">This result came from the draft blueprint, not a published version.</Alert>
        )}
        {r.mode === 'set' && r.setVersion === 'DRAFT' && r.status === 'SUCCEEDED' && (
          <Alert type="info">This result came from the draft set, not a published set version.</Alert>
        )}
        {result.data && !result.data.expired && (
          <Grid gridDefinition={[{ colspan: { default: 12, l: 5 } }, { colspan: { default: 12, l: 7 } }]}>
            <Container header={<Header variant="h2">Document</Header>}>
              <DocumentViewer url={result.data.documentUrl} contentType={r.contentType} name={r.fileName} goTo={goTo} />
            </Container>
            {isSetRun && result.data.set ? (
              <SetResultView
                result={result.data.result}
                snapshot={result.data.set}
                baseName={safeName(`${r.setName ?? r.blueprintName}-${r.fileName}`)}
                label={isSetPreview ? 'preview' : 'run'}
                contentType={r.contentType}
                onShowPage={showPage}
              />
            ) : (
              <ResultView
                result={result.data.result}
                baseName={safeName(`${r.blueprintName}-${r.fileName}`)}
                allowed={allowed}
                preview={preview}
                onShowPage={showPage}
              />
            )}
          </Grid>
        )}
        {result.isLoading && r.status === 'SUCCEEDED' && <Spinner />}
      </SpaceBetween>
    </ContentLayout>
  );
}
